import { useState } from "react";

import { FormReceitaManual } from "../receitas/FormReceitaManual";
import { SeletorEmpresaReceita } from "../receitas/SeletorEmpresaReceita";
import { useTemPermissao } from "../../lib/auth/permissoes";
import { useCriarReceitaManual } from "../../lib/receitas/hooks";
import type { ReceitaManualIn } from "../../lib/receitas/tipos";

/*
 * Página de lançamento de receita manual (Tela 5 §10.5).
 *
 * Estrutura: gate de permissão (`criar_receita_manual`) → seletor de empresa
 * (EMPRESA-only, sem escopo de grupo) → contexto → formulário. Enquanto nenhuma
 * empresa está selecionada, o formulário não aparece (mensagem de seleção).
 *
 * O hook de mutação é fixado à empresa em foco; o `isSuccess` controla o banner
 * verde do form. Trocar de empresa reseta o estado da mutação (o `key={empresaId}`
 * remonta o `FormularioDaEmpresa`) — o banner de sucesso some.
 */

/** Ação RBAC desta tela — string EXATA do StrEnum do backend. */
const ACAO_CRIAR_RECEITA_MANUAL = "criar_receita_manual";

export function ReceitaManual(): JSX.Element {
  const podeLancar = useTemPermissao(ACAO_CRIAR_RECEITA_MANUAL);

  if (!podeLancar) {
    return (
      <section className="flex flex-col gap-6">
        <h1 className="font-titulo text-2xl text-off-black">Receita manual</h1>
        <p className="font-corpo text-base text-off-black">
          Você não tem permissão para lançar receitas.
        </p>
      </section>
    );
  }

  return <ReceitaManualHabilitada />;
}

/**
 * Corpo da tela quando o usuário TEM a permissão. Separado para que o
 * `useCriarReceitaManual` (e o seletor que dispara fetch) só montem quando a tela
 * está habilitada — sem chamar hooks condicionalmente no componente de gate.
 */
function ReceitaManualHabilitada(): JSX.Element {
  const [empresaId, setEmpresaId] = useState<string | null>(null);

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-titulo text-2xl text-off-black">Receita manual</h1>

      <SeletorEmpresaReceita
        selecionada={empresaId}
        aoSelecionar={setEmpresaId}
      />

      {empresaId === null ? (
        <p className="font-corpo text-base text-off-black">
          Selecione uma empresa para lançar uma receita.
        </p>
      ) : (
        <FormularioDaEmpresa key={empresaId} empresaId={empresaId} />
      )}
    </section>
  );
}

/**
 * Orquestra a mutação para uma empresa fixa. `key={empresaId}` no caller garante
 * que trocar de empresa remonta este componente — o estado da mutação (banner de
 * sucesso/erro) reinicia, sem vazar o sucesso de uma empresa para outra.
 */
function FormularioDaEmpresa({
  empresaId,
}: {
  empresaId: string;
}): JSX.Element {
  const mutacao = useCriarReceitaManual(empresaId);

  function aoLancar(dados: ReceitaManualIn): void {
    mutacao.mutate(dados);
  }

  return (
    <FormReceitaManual
      key={mutacao.isSuccess ? "lancada" : "form"}
      aoLancar={aoLancar}
      enviando={mutacao.isPending}
      erro={mutacao.error}
      sucesso={mutacao.isSuccess}
    />
  );
}
